import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getStoredToken, setStoredToken, clearStoredToken } from '../services/apiClient.js';
import * as authService from '../services/authService.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await authService.me();
      setUser(res?.data || null);
    } catch {
      clearStoredToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const login = async (credentials) => {
    const res = await authService.login(credentials);
    const { token, user: userData } = res?.data || {};
    if (token) setStoredToken(token);
    setUser(userData || null);
    return userData;
  };

  // Admin / marketer login. The API includes a `redirectTo` the caller can use.
  const adminLogin = async (credentials) => {
    const res = await authService.adminLogin(credentials);
    const { token, user: userData, redirectTo } = res?.data || {};
    if (token) setStoredToken(token);
    setUser(userData || null);
    return { user: userData, redirectTo };
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // ignore - still clear locally
    }
    clearStoredToken();
    setUser(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    role: user?.role || null,
    login,
    adminLogin,
    logout,
    refresh: loadProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
